import React, { useState, useEffect } from 'react';
import {
  Button,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Avatar,
  Tooltip,
  Snackbar,
  Skeleton,
  Fade,
  Slide,
  Badge,
  InputAdornment
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Group as GroupIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import admin_api from '../../../api/admin';
import apiConfig from '../../../api/apiConfig';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';

// Styles using makeStyles for MUI v4
const useStyles = makeStyles((theme) => ({
  styledContainer: {
    backgroundColor: '#12192c',
    minHeight: 'calc(100vh - 100px)',
    padding: '20px',
    color: '#ffffff',
  },
  styledPaper: {
    backgroundColor: '#1a2235',
    color: '#ffffff',
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    border: '1px solid #2d3748',
  },
  styledCard: {
    backgroundColor: '#1e293b',
    color: '#ffffff',
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
    border: '1px solid #2d3748',
    height: '100%',
  },
  headerCard: {
    background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
    color: 'white',
    borderRadius: theme.spacing(2),
    marginBottom: theme.spacing(4),
    boxShadow: '0 10px 30px rgba(6, 182, 212, 0.3)',
  },
  styledTextField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: theme.spacing(1),
      '& fieldset': {
        borderColor: '#2d3748',
      },
      '&:hover fieldset': {
        borderColor: '#4a5568',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#06b6d4',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#a0aec0',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#06b6d4',
    },
    '& .MuiOutlinedInput-input': {
      color: '#e2e8f0',
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
      color: '#06b6d4',
    },
    '& .MuiFormHelperText-root': {
      color: '#a0aec0',
    },
    '& .MuiFormHelperText-root.Mui-error': {
      color: '#f56565',
    },
  },
  dialogPaper: {
    backgroundColor: '#1a2235',
    color: '#ffffff',
    borderRadius: theme.spacing(2),
    border: '1px solid #2d3748',
  },
  styledButton: {
    background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
    color: 'white',
    borderRadius: theme.spacing(3),
    textTransform: 'none',
    fontWeight: 500,
    boxShadow: '0 4px 15px rgba(6, 182, 212, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
      boxShadow: '0 6px 20px rgba(6, 182, 212, 0.4)',
      transform: 'translateY(-2px)',
    },
    '&:disabled': {
      background: theme.palette.grey[700],
      boxShadow: 'none',
    },
  },
  tableHead: {
    '& .MuiTableCell-head': {
      backgroundColor: '#1e293b',
      color: '#06b6d4',
      fontWeight: 'bold',
      borderBottom: '2px solid #2d3748'
    }
  },
  tableRow: {
    '& .MuiTableCell-root': {
      color: '#e2e8f0',
      borderColor: '#2d3748'
    },
    '&:hover': {
      backgroundColor: 'rgba(6, 182, 212, 0.05)'
    }
  },
  skeletonDark: {
    backgroundColor: '#2d3748',
  }
}));

export default function UsersManagement() {
  const classes = useStyles();
  const [admins, setAdmins] = useState([]);
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [newAdmin, setNewAdmin] = useState({
    email: '',
    name: '',
    phone: ''
  });
  const [formErrors, setFormErrors] = useState({
    email: '',
    name: '',
    phone: ''
  });
  const { isCollapse } = useCollapseDrawer();
  
  const DRAWER_WIDTH = 280;
  const COLLAPSE_WIDTH = 102;
  const MAX_ADMINS = 2;

  const containerStyle = {
    marginTop: '80px',
    marginLeft: isCollapse ? `${COLLAPSE_WIDTH + 24}px` : `${DRAWER_WIDTH + 24}px`,
    marginRight: '24px',
    transition: 'all 0.3s ease',
  };

  if (window.innerWidth <= 1024) {
    containerStyle.marginLeft = '24px';
  }

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let response;
      
      if (typeof admin_api.get_admins === 'function') {
        response = await admin_api.get_admins();
      } else if (typeof admin_api.getAdmins === 'function') {
        response = await admin_api.getAdmins();
      } else if (typeof admin_api.list_users === 'function') {
        response = await admin_api.list_users({ role: 'admin' });
      } else {
        response = await apiConfig.fetchWithAuth('/admin/users?role=admin');
      }
      
      if (response && response.data) {
        let adminData;
        
        if (Array.isArray(response.data)) {
          adminData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          adminData = response.data.data;
        } else if (response.data.success && response.data.data && Array.isArray(response.data.data)) {
          adminData = response.data.data;
        } else if (response.data.users && Array.isArray(response.data.users)) {
          adminData = response.data.users;
        } else {
          console.warn('Unexpected admin data format:', response.data);
          adminData = [];
        }
        
        const formattedAdmins = adminData.map(admin => ({
          _id: admin.id || admin._id,
          id: admin.id || admin._id,
          name: admin.name || admin.Name || admin.username || admin.userName || '',
          email: admin.email || '',
          phone: admin.phone || admin.phoneNumber || '',
          status: admin.status || 'active',
          createdAt: admin.created_at || admin.createdAt || new Date().toISOString()
        }));
        
        setAdmins(formattedAdmins);
        setFilteredAdmins(formattedAdmins);
        setSuccessMessage('Admin users loaded successfully');
      } else {
        setAdmins([]);
        setFilteredAdmins([]);
      }
    } catch (err) {
      console.error('Error fetching admins:', err);
      setError('Failed to fetch admin users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter admins based on search query
  useEffect(() => {
    if (!searchQuery) {
      setFilteredAdmins(admins);
    } else {
      const filtered = admins.filter(admin =>
        admin.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        admin.email.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredAdmins(filtered);
    }
  }, [admins, searchQuery]);

  const validateForm = () => {
    const errors = {
      name: '',
      email: '',
      phone: ''
    };
    let isValid = true;
    
    if (!newAdmin.name.trim()) {
      errors.name = 'Name is required';
      isValid = false;
    }
    
    if (!newAdmin.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(newAdmin.email)) {
      errors.email = 'Email is invalid';
      isValid = false;
    }
    
    if (newAdmin.phone && !/^[0-9+\-\s()]{7,15}$/.test(newAdmin.phone)) {
      errors.phone = 'Please enter a valid phone number';
      isValid = false;
    }
    
    setFormErrors(errors);
    return isValid;
  };

  const handleAddAdmin = async () => {
    if (!Array.isArray(admins)) {
      setError('Unable to add admin - please refresh the page');
      return;
    }
    
    if (admins.length >= MAX_ADMINS) {
      setError('Maximum number of admins reached (2)');
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    try {
      setActionLoading(true);
      
      let response;
      if (typeof admin_api.add_admin === 'function') {
        response = await admin_api.add_admin(newAdmin);
      } else if (typeof admin_api.addAdmin === 'function') {
        response = await admin_api.addAdmin(newAdmin);
      } else if (typeof admin_api.create_user === 'function') {
        response = await admin_api.create_user({
          ...newAdmin,
          role: 'admin'
        });
      } else {
        response = await apiConfig.fetchWithAuth('/admin/users', {
          method: 'POST',
          body: JSON.stringify({
            ...newAdmin,
            role: 'admin'
          })
        });
      }
      
      if (response && (response.data?.success || response.status === 200 || response.status === 201)) {
        await fetchAdmins();
        setOpenDialog(false);
        setNewAdmin({ email: '', name: '', phone: '' });
        setFormErrors({ email: '', name: '', phone: '' });
        setSuccessMessage('Admin added successfully');
      } else {
        const errorMsg = response?.data?.message || 'Failed to add admin';
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Error adding admin:', err);
      setError(err.message || 'Failed to add admin');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteClick = (admin) => {
    setSelectedAdmin(admin);
    setOpenDeleteDialog(true);
  };

  const handleDeleteAdmin = async () => {
    if (!selectedAdmin) return;
    
    try {
      setActionLoading(true);
      
      let response;
      if (typeof admin_api.delete_admin === 'function') {
        response = await admin_api.delete_admin(selectedAdmin._id || selectedAdmin.id);
      } else if (typeof admin_api.deleteAdmin === 'function') {
        response = await admin_api.deleteAdmin(selectedAdmin._id || selectedAdmin.id);
      } else if (typeof admin_api.remove_user === 'function') {
        response = await admin_api.remove_user(selectedAdmin._id || selectedAdmin.id);
      } else {
        response = await apiConfig.fetchWithAuth(`/admin/users/${selectedAdmin._id || selectedAdmin.id}`, {
          method: 'DELETE'
        });
      }
      
      if (response && (response.data?.success || response.status === 200 || response.status === 204)) {
        await fetchAdmins();
        setSuccessMessage('Admin removed successfully');
      } else {
        setError('Failed to delete admin: ' + (response?.data?.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error deleting admin:', err);
      setError('Failed to delete admin: ' + (err.message || 'Unknown error'));
    } finally {
      setActionLoading(false);
      setOpenDeleteDialog(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  useEffect(() => {
    const controller = new AbortController();
    
    fetchAdmins();
    
    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
        setError(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  return (
    <Box className={classes.styledContainer} style={containerStyle}>
      {/* Success Message */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" style={{ backgroundColor: '#22c55e' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Message */}
      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" style={{ backgroundColor: '#ef4444' }}>
          {error}
        </Alert>
      </Snackbar>

      <Fade in timeout={800}>
        <Box>
          {/* Header */}
          <Card className={classes.headerCard}>
            <CardContent style={{ padding: '32px' }}>
              <Grid container alignItems="center" justify="space-between">
                <Grid item>
                  <Typography variant="h3" style={{ fontWeight: 700, marginBottom: '8px' }}>
                    Admin Users Management
                  </Typography>
                  <Typography variant="h6" style={{ opacity: 0.9 }}>
                    Manage administrators who can access the dashboard
                  </Typography>
                </Grid>
                <Grid item>
                  <Box style={{ display: 'flex', gap: '16px' }}>
                    <IconButton
                      onClick={fetchAdmins}
                      disabled={loading}
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                      }}
                    >
                      <RefreshIcon />
                    </IconButton>
                    <Button
                      className={classes.styledButton}
                      startIcon={<PersonAddIcon />}
                      onClick={() => setOpenDialog(true)}
                      disabled={Array.isArray(admins) && admins.length >= MAX_ADMINS}
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      Add Admin
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          <Grid container spacing={3} style={{ marginBottom: '32px' }}>
            <Grid item xs={12} md={4}>
              <Slide direction="up" in timeout={1000}>
                <Card className={classes.styledCard}>
                  <CardContent style={{ textAlign: 'center', padding: '24px' }}>
                    <Badge badgeContent={Array.isArray(admins) ? admins.length : 0} color="primary" max={99}>
                      <GroupIcon style={{ fontSize: 48, color: '#06b6d4', marginBottom: '16px' }} />
                    </Badge>
                    <Typography variant="h6" gutterBottom style={{ color: '#e2e8f0' }}>
                      Current Admins
                    </Typography>
                    <Typography variant="h4" style={{ color: '#06b6d4', fontWeight: 'bold' }}>
                      {Array.isArray(admins) ? admins.length : 0}
                    </Typography>
                    <Typography style={{ color: '#a0aec0', marginTop: '8px', fontSize: '0.875rem' }}>
                      Active administrators
                    </Typography>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Slide direction="up" in timeout={1200}>
                <Card className={classes.styledCard}>
                  <CardContent style={{ textAlign: 'center', padding: '24px' }}>
                    <SecurityIcon style={{ fontSize: 48, color: '#22c55e', marginBottom: '16px' }} />
                    <Typography variant="h6" gutterBottom style={{ color: '#e2e8f0' }}>
                      Available Slots
                    </Typography>
                    <Typography variant="h4" style={{ 
                      color: MAX_ADMINS - (Array.isArray(admins) ? admins.length : 0) > 0 ? '#22c55e' : '#ef4444',
                      fontWeight: 'bold'
                    }}>
                      {MAX_ADMINS - (Array.isArray(admins) ? admins.length : 0)}
                    </Typography>
                    <Typography style={{ color: '#a0aec0', marginTop: '8px', fontSize: '0.875rem' }}>
                      Out of {MAX_ADMINS} maximum
                    </Typography>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Slide direction="up" in timeout={1400}>
                <Card className={classes.styledCard}>
                  <CardContent style={{ padding: '24px' }}>
                    <Typography variant="h6" gutterBottom style={{ color: '#06b6d4', fontWeight: 'bold' }}>
                      Quick Actions
                    </Typography>
                    <Typography variant="body2" style={{ color: '#a0aec0', marginBottom: '16px' }}>
                      Search and manage your admin team
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search admins..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      variant="outlined"
                      className={classes.styledTextField}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
          </Grid>

          {/* Admins Table */}
          <Slide direction="up" in timeout={1600}>
            <TableContainer component={Paper} className={classes.styledPaper}>
              {loading ? (
                <Box style={{ padding: '32px' }}>
                  {[...Array(3)].map((_, index) => (
                    <Box key={index} style={{ display: 'flex', gap: '16px', marginBottom: '16px', alignItems: 'center' }}>
                      <Skeleton variant="circle" width={48} height={48} className={classes.skeletonDark} />
                      <Skeleton variant="text" width="25%" height={30} className={classes.skeletonDark} />
                      <Skeleton variant="text" width="30%" height={30} className={classes.skeletonDark} />
                      <Skeleton variant="text" width="15%" height={30} className={classes.skeletonDark} />
                      <Skeleton variant="rect" width="10%" height={25} className={classes.skeletonDark} />
                      <Skeleton variant="text" width="15%" height={30} className={classes.skeletonDark} />
                      <Skeleton variant="rect" width="5%" height={30} className={classes.skeletonDark} />
                    </Box>
                  ))}
                </Box>
              ) : !Array.isArray(filteredAdmins) || filteredAdmins.length === 0 ? (
                <Box style={{ padding: '48px', textAlign: 'center' }}>
                  <GroupIcon style={{ fontSize: 60, color: '#4a5568', marginBottom: '16px' }} />
                  <Typography variant="h6" style={{ color: '#e2e8f0', marginBottom: '8px' }}>
                    {searchQuery ? 'No admins match your search' : 'No admins found'}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#a0aec0', marginBottom: '24px' }}>
                    {searchQuery 
                      ? 'Try adjusting your search criteria or clear the search to see all admins.'
                      : 'Add up to 2 admins to help manage your court booking system.'}
                  </Typography>
                  {searchQuery ? (
                    <Button
                      variant="outlined"
                      onClick={() => setSearchQuery('')}
                      style={{
                        color: '#e2e8f0',
                        borderColor: '#4a5568',
                      }}
                    >
                      Clear Search
                    </Button>
                  ) : (
                    <Button
                      className={classes.styledButton}
                      startIcon={<PersonAddIcon />}
                      onClick={() => setOpenDialog(true)}
                    >
                      Add Your First Admin
                    </Button>
                  )}
                </Box>
              ) : (
                <Table>
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell>Administrator</TableCell>
                      <TableCell>Contact Information</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Added Date</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAdmins.map((admin, index) => (
                      <Fade in timeout={300 + index * 100} key={admin._id || admin.id || index}>
                        <TableRow className={classes.tableRow}>
                          <TableCell>
                            <Box style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                              <Avatar 
                                style={{ 
                                  backgroundColor: admin.status === 'active' ? '#06b6d4' : '#4a5568',
                                  width: 48,
                                  height: 48,
                                  fontSize: '1.2rem',
                                  fontWeight: 'bold'
                                }}
                              >
                                {getInitials(admin.name)}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                                  {admin.name}
                                </Typography>
                                <Box style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <SecurityIcon style={{ fontSize: 16, color: '#06b6d4' }} />
                                  <Typography variant="caption" style={{ color: '#a0aec0' }}>
                                    Administrator
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Box style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                <EmailIcon style={{ fontSize: 16, color: '#06b6d4' }} />
                                <Typography variant="body2">{admin.email}</Typography>
                              </Box>
                              {admin.phone && (
                                <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <PhoneIcon style={{ fontSize: 16, color: '#06b6d4' }} />
                                  <Typography variant="body2" style={{ color: '#a0aec0' }}>
                                    {admin.phone}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={admin.status || 'active'} 
                              size="small"
                              style={{
                                backgroundColor: admin.status === 'active' 
                                  ? 'rgba(34, 197, 94, 0.1)' 
                                  : 'rgba(156, 163, 175, 0.1)',
                                color: admin.status === 'active' ? '#22c55e' : '#9ca3af',
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" style={{ color: '#a0aec0' }}>
                              {formatDate(admin.createdAt)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Remove Admin">
                              <IconButton
                                onClick={() => handleDeleteClick(admin)}
                                disabled={actionLoading}
                                style={{ color: '#ef4444' }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      </Fade>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </Slide>
        </Box>
      </Fade>

      {/* Add Admin Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => !actionLoading && setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          className: classes.dialogPaper
        }}
      >
        <DialogTitle style={{ 
          borderBottom: '1px solid #2d3748',
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
          <PersonAddIcon style={{ color: '#06b6d4' }} />
          <Box>
            <Typography variant="h6">Add New Admin</Typography>
            <Typography variant="body2" style={{ color: '#a0aec0', marginTop: '4px' }}>
              Add a new administrator to help manage your court bookings
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent style={{ padding: '32px' }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                fullWidth
                label="Full Name"
                type="text"
                required
                value={newAdmin.name}
                onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                error={!!formErrors.name}
                helperText={formErrors.name}
                disabled={actionLoading}
                variant="outlined"
                className={classes.styledTextField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                required
                value={newAdmin.email}
                onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                error={!!formErrors.email}
                helperText={formErrors.email}
                disabled={actionLoading}
                variant="outlined"
                className={classes.styledTextField}
                InputProps={{
                  startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Phone Number"
              type="tel"
              value={newAdmin.phone}
              onChange={(e) => setNewAdmin({ ...newAdmin, phone: e.target.value })}
              error={!!formErrors.phone}
              helperText={formErrors.phone || "Optional - for emergency contact"}
              disabled={actionLoading}
              variant="outlined"
              className={classes.styledTextField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        
        {/* Warning Message */}
        {Array.isArray(admins) && admins.length >= MAX_ADMINS - 1 && (
          <Box style={{ 
            marginTop: '24px', 
            padding: '16px', 
            backgroundColor: 'rgba(245, 101, 101, 0.1)', 
            borderRadius: '8px',
            border: '1px solid rgba(245, 101, 101, 0.2)'
          }}>
            <Typography variant="body2" style={{ color: '#f56565', fontWeight: 'bold' }}>
              ⚠️ Maximum Limit Notice
            </Typography>
            <Typography variant="body2" style={{ color: '#a0aec0', marginTop: '4px' }}>
              You can only have a maximum of {MAX_ADMINS} administrators. This will be your last admin slot.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions style={{ padding: '24px', borderTop: '1px solid #2d3748' }}>
        <Button
          onClick={() => {
            setOpenDialog(false);
            setNewAdmin({ email: '', name: '', phone: '' });
            setFormErrors({ email: '', name: '', phone: '' });
          }}
          disabled={actionLoading}
          style={{ color: '#a0aec0' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleAddAdmin}
          disabled={actionLoading}
          className={classes.styledButton}
          startIcon={actionLoading ? <CircularProgress size={20} /> : <PersonAddIcon />}
        >
          {actionLoading ? 'Adding...' : 'Add Admin'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog
      open={openDeleteDialog}
      onClose={() => !actionLoading && setOpenDeleteDialog(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: classes.dialogPaper
      }}
    >
      <DialogTitle style={{ 
        borderBottom: '1px solid #2d3748',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        <DeleteIcon style={{ color: '#ef4444' }} />
        <Box>
          <Typography variant="h6">Remove Administrator</Typography>
          <Typography variant="body2" style={{ color: '#a0aec0', marginTop: '4px' }}>
            This action cannot be undone
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent style={{ padding: '32px' }}>
        <Box style={{ 
          padding: '24px',
          backgroundColor: 'rgba(245, 101, 101, 0.1)',
          borderRadius: '8px',
          border: '1px solid rgba(245, 101, 101, 0.2)',
          marginBottom: '24px'
        }}>
          <Typography variant="body1" style={{ color: '#f56565', fontWeight: 'bold', marginBottom: '8px' }}>
            ⚠️ Warning
          </Typography>
          <Typography variant="body2" style={{ color: '#a0aec0' }}>
            This will permanently remove the administrator's access to the dashboard. 
            They will no longer be able to manage bookings or access admin features.
          </Typography>
        </Box>
        
        {selectedAdmin && (
          <Box>
            <Typography variant="body1" style={{ marginBottom: '16px' }}>
              Are you sure you want to remove the following administrator?
            </Typography>
            <Box style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '16px',
              padding: '16px',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              border: '1px solid #2d3748'
            }}>
              <Avatar 
                style={{ 
                  backgroundColor: '#ef4444',
                  width: 48,
                  height: 48,
                  fontSize: '1.2rem',
                  fontWeight: 'bold'
                }}
              >
                {getInitials(selectedAdmin.name)}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: '#e2e8f0' }}>
                  {selectedAdmin.name}
                </Typography>
                <Typography variant="body2" style={{ color: '#a0aec0' }}>
                  {selectedAdmin.email}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions style={{ padding: '24px', borderTop: '1px solid #2d3748' }}>
        <Button
          onClick={() => setOpenDeleteDialog(false)}
          disabled={actionLoading}
          style={{ color: '#a0aec0' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleDeleteAdmin}
          disabled={actionLoading}
          startIcon={actionLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          style={{
            backgroundColor: '#ef4444',
            color: 'white',
            '&:hover': {
              backgroundColor: '#dc2626',
            }
          }}
        >
          {actionLoading ? 'Removing...' : 'Remove Admin'}
        </Button>
      </DialogActions>
    </Dialog>
  </Box>
);
}